import { useMemo } from 'react';
import { Location, SchoolResult } from '@/graphql/types';
import { calculateDistance } from '@/utils/calculateDistance';

interface SchoolsListProps {
  schools: SchoolResult[];
  centerLocation: Location;
  metricType: string;
}

export const SchoolsList: React.FC<SchoolsListProps> = ({
  schools,
  centerLocation,
  metricType,
}) => {
  const { totalSchools, inDistrictCount, outsideDistrictCount, sortedSchools } = useMemo(() => {
    const inDistrict = schools.filter(school => school.isPin);
    const outsideDistrict = schools.filter(school => !school.isPin);
    const total = schools.length;

    const sorted = [...schools].sort((a, b) => {
      const distanceA = calculateDistance(centerLocation, a.location, metricType as 'mi' | 'km');
      const distanceB = calculateDistance(centerLocation, b.location, metricType as 'mi' | 'km');
      return distanceA - distanceB;
    });

    return {
      totalSchools: total,
      inDistrictCount: inDistrict.length,
      outsideDistrictCount: outsideDistrict.length,
      sortedSchools: sorted,
    };
  }, [schools, centerLocation, metricType]);

  if (totalSchools === 0) {
    return (
      <div className="visually-hidden">
        <h2>Schools within selected radius</h2>
        <p>No schools found within the selected radius.</p>
      </div>
    );
  }

  return (
    <div className="visually-hidden">
      <h2>Schools within selected radius</h2>
      <div role="status" aria-live="polite">
        <p>
          <strong>Total schools:</strong> {totalSchools}
        </p>
        <p>
          <strong>In district:</strong> {inDistrictCount}
        </p>
        <p>
          <strong>Outside district:</strong> {outsideDistrictCount}
        </p>
      </div>
      <ul>
        {sortedSchools.map((school, index) => {
          const distance = calculateDistance(
            centerLocation,
            school.location,
            metricType as 'mi' | 'km'
          );
          const distanceUnit = metricType === 'mi' ? 'miles' : 'kilometers';
          const districtStatus = school.isPin ? 'In district' : 'Outside district';

          return (
            <li key={school.schoolId || index}>
              <p>
                <strong>School name:</strong> {school.name || `School ${index + 1}`}
              </p>
              <p>
                <strong>Distance:</strong> {distance} {distanceUnit}
              </p>
              <p>
                <strong>District status:</strong> {districtStatus}
              </p>
              {school.address && (
                <p>
                  <strong>Address:</strong> {school.address}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
